import React, { createContext, useContext, useMemo } from 'react';
import { resolveModuleStates } from '../utils/storeModules';

/**
 * Per-module state for the funeral home currently signed in.
 *
 * ResellerDashboard already fetches the store, so this provider costs nothing
 * extra and is mounted there, inside the `!store` guard. It exists rather than
 * prop drilling for three reasons: the Infinity Touch pages receive no `store`
 * prop at all today (PrintOrderFlow refetches /stores/my by itself), the terms
 * gate needs exactly the same data, and drilling would mean threading a prop
 * through every route entry — where forgetting one leaves a page quietly
 * writable.
 */
const ModuleContext = createContext(null);

export const ModuleProvider = ({ store, refreshStore, children }) => {
    const value = useMemo(() => {
        const modules = resolveModuleStates(store);
        return {
            store,
            modules,
            refreshStore,
            /** May this funeral home write to the named module right now? */
            canWrite: (moduleName) => modules[moduleName]?.enabled === true,
            /** Show the module's pages, but with every write disabled? */
            isReadOnly: (moduleName) => modules[moduleName]?.readOnly === true,
            /** Should the module appear in the navigation at all? */
            isVisible: (moduleName) => modules[moduleName]?.visible === true
        };
    }, [store, refreshStore]);

    return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
};

/**
 * Outside the funeral-home panel there is no store, so the hook answers
 * "everything is writable" rather than throwing — that keeps shared components
 * usable from the admin screens and the storefront without a null check at
 * every call site.
 */
const OPEN = {
    store: null,
    modules: resolveModuleStates(null),
    refreshStore: () => {},
    canWrite: () => true,
    isReadOnly: () => false,
    isVisible: () => true
};

export const useModules = () => useContext(ModuleContext) || OPEN;

export default ModuleContext;
